import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import AppHeader from './AppHeader';
import { enrollmentAPI, sessionAPI } from '../services/api';
import { useClearOnSiteChange, useCurrentSiteId } from '../hooks/useCurrentSiteId';

const GROUP_LABEL = {
  school_age: 'School-age',
  ei: 'EI',
  itinerant: 'Itinerant',
};

const SERVICE_LABEL = {
  center_based: 'Center-based',
  related_service: 'Related service',
  seit: 'SEIT',
  unknown: 'Unknown',
};

function formatChange(value) {
  if (value == null || value === '') return '';
  if (value > 0) return `+${value}`;
  return String(value);
}

const ROSTER_STATUS_LABEL = {
  maintained: 'Maintained in SYMS',
  partial: 'Partial roster',
  not_maintained: 'Not maintained in SYMS',
};

function siteHeadline(block, anchorName) {
  if (block.empty_session_message) return block.empty_session_message;
  if (block.site.roster_status === 'not_maintained') {
    if (block.join_reason) return block.join_reason;
    return block.roster_source || 'Not maintained in SYMS';
  }
  if (block.join_reason) return block.join_reason;
  if (!block.totals) {
    if (block.session_match === 'ambiguous') {
      return `More than one session is named “${anchorName}” at this site.`;
    }
    if (block.session_match === 'none') {
      return `No session named “${anchorName}” at this site.`;
    }
    return `No session named “${anchorName}” at this site.`;
  }
  const totals = block.totals;
  const numbers = `School-age ${totals.school_age} · EI ${totals.ei} · Itinerant ${totals.itinerant} · Total ${totals.all}`;
  const source = block.roster_source || 'Derived from the roster';
  const bridge = /[.!?]$/.test(source) ? ' ' : '. ';
  return `${source}${bridge}${numbers}`;
}

function csvEscape(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function downloadCsv(filename, rows) {
  const header = [
    'site', 'roster_status', 'code', 'label', 'group', 'count', 'previous_count', 'change', 'session_match',
  ];
  const lines = [header.join(',')];
  rows.forEach((row) => {
    lines.push(header.map((key) => csvEscape(row[key])).join(','));
  });
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const EnrollmentDashboard = () => {
  const siteId = useCurrentSiteId();
  const [scope, setScope] = useState(siteId ? 'site' : 'all');
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openSites, setOpenSites] = useState({});
  const [drill, setDrill] = useState(null);

  useClearOnSiteChange(siteId, () => {
    setReport(null);
    setSessions([]);
    setSessionId('');
    setError('');
    setOpenSites({});
    setDrill(null);
    if (!siteId) setScope('all');
  });

  const requestConfig = useMemo(
    () => (scope === 'all' || !siteId ? { omitSiteHeader: true } : {}),
    [scope, siteId],
  );

  const loadSessions = useCallback(async () => {
    const res = await sessionAPI.getSessions({}, requestConfig);
    const list = res.data.results || res.data || [];
    setSessions(Array.isArray(list) ? list : []);
    return Array.isArray(list) ? list : [];
  }, [requestConfig]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await loadSessions();
        if (cancelled) return;
        setSessionId((current) => {
          const visible = scope === 'site' && siteId
            ? list.filter((session) => session.site === siteId)
            : list;
          if (visible.some((session) => String(session.id) === String(current))) return current;
          const active = visible.find((session) => session.is_active);
          return active ? String(active.id) : (visible[0] ? String(visible[0].id) : '');
        });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load sessions.');
      }
    })();
    return () => { cancelled = true; };
  }, [loadSessions, scope, siteId]);

  useEffect(() => {
    if (!sessionId) {
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = { session: sessionId };
        if (scope === 'site' && siteId) params.sites = String(siteId);
        const res = await enrollmentAPI.summary(params, requestConfig);
        if (!cancelled) setReport(res.data);
      } catch (err) {
        if (!cancelled) {
          setReport(null);
          const detail = err.response?.data?.detail;
          setError(detail || err.message || 'Could not load the enrollment report.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, scope, siteId, requestConfig]);

  const visibleSessions = useMemo(() => {
    if (scope === 'site' && siteId) return sessions.filter((session) => session.site === siteId);
    return sessions;
  }, [sessions, scope, siteId]);

  const fundingMix = useMemo(() => {
    const totals = {};
    (report?.by_site || []).forEach((block) => {
      (block.by_code || []).forEach((row) => {
        if (!totals[row.code]) {
          totals[row.code] = { ...row, count: 0, previous_count: 0, change: 0 };
        }
        totals[row.code].count += row.count;
        totals[row.code].previous_count += row.previous_count;
        totals[row.code].change += row.change;
      });
    });
    return Object.values(totals);
  }, [report]);

  const openDrill = async (block, code, label) => {
    if (!block.session?.id) return;
    setDrill({ label, siteName: block.site.name, loading: true, rows: [], error: '' });
    try {
      const params = {
        session: sessionId,
        site: block.site.id,
        code,
        as_of: report?.as_of,
      };
      const res = await enrollmentAPI.students(params, requestConfig);
      setDrill({
        label,
        siteName: block.site.name,
        loading: false,
        rows: res.data.students || [],
        count: res.data.count,
        error: '',
      });
    } catch (err) {
      setDrill({
        label,
        siteName: block.site.name,
        loading: false,
        rows: [],
        error: err.response?.data?.detail || err.message || 'Could not load students.',
      });
    }
  };

  const exportCsv = () => {
    if (!report) return;
    const rows = [];
    report.by_site.forEach((block) => {
      if (block.empty_session_message) {
        rows.push({
          site: block.site.name,
          roster_status: block.site.roster_status,
          code: '',
          label: block.empty_session_message,
          group: '',
          count: 'no students',
          previous_count: '',
          change: '',
          session_match: block.session_match,
        });
        return;
      }
      if (block.site.roster_status === 'not_maintained') {
        rows.push({
          site: block.site.name,
          roster_status: block.site.roster_status,
          code: '',
          label: block.roster_source || 'Not maintained in SYMS',
          group: '',
          count: 'not maintained',
          previous_count: '',
          change: '',
          session_match: block.session_match,
        });
        if (block.unmapped != null) {
          rows.push({
            site: block.site.name,
            roster_status: block.site.roster_status,
            code: 'UNMAPPED',
            label: 'Unmapped',
            group: '',
            count: block.unmapped,
            previous_count: '',
            change: block.unmapped_change == null ? '' : block.unmapped_change,
            session_match: block.session_match,
          });
        }
        return;
      }
      if (!(block.by_code || []).length && block.join_reason) {
        rows.push({
          site: block.site.name,
          roster_status: block.site.roster_status,
          code: '',
          label: block.join_reason,
          group: '',
          count: '',
          previous_count: '',
          change: '',
          session_match: block.session_match,
        });
        return;
      }
      (block.by_code || []).forEach((row) => {
        rows.push({
          site: block.site.name,
          roster_status: block.site.roster_status,
          code: row.code,
          label: row.label,
          group: row.group,
          count: row.count,
          previous_count: row.previous_count,
          change: row.change,
          session_match: block.session_match,
        });
      });
      if (block.unmapped != null) {
        rows.push({
          site: block.site.name,
          roster_status: block.site.roster_status,
          code: 'UNMAPPED',
          label: 'Unmapped',
          group: '',
          count: block.unmapped,
          previous_count: '',
          change: block.unmapped_change == null ? '' : block.unmapped_change,
          session_match: block.session_match,
        });
      }
    });
    downloadCsv(`enrollment-${report.session?.name || 'report'}-${report.as_of}.csv`, rows);
  };

  const totals = report?.totals;

  return (
    <Box>
      <AppHeader backTo="/" />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>Enrollment and funding</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Counts come from the roster. Change is against the last day of the previous month
          {report?.previous_as_of ? ` (${report.previous_as_of})` : ''}.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={scope}
            onChange={(_event, value) => {
              if (!value) return;
              setScope(value);
              setReport(null);
            }}
          >
            <ToggleButton value="all">All sites</ToggleButton>
            <ToggleButton value="site" disabled={!siteId}>This site</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="enrollment-session-label">Session</InputLabel>
            <Select
              labelId="enrollment-session-label"
              label="Session"
              value={visibleSessions.some((session) => String(session.id) === String(sessionId)) ? sessionId : ''}
              onChange={(event) => setSessionId(event.target.value)}
            >
              {visibleSessions.map((session) => (
                <MenuItem key={session.id} value={String(session.id)}>
                  {session.site_name ? `${session.name} — ${session.site_name}` : session.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} disabled={!report}>
            Export CSV
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <CircularProgress size={28} />}

        {report && Array.isArray(report.not_captured) && report.not_captured.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Vacancies, typical-child enrolment and mandated hours are not yet captured in SYMS.
          </Alert>
        )}
        {report && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Change uses enrolment and discharge dates. It does not show a mid-year program transfer, and a status change with no discharge date is not counted as a decrease.
          </Alert>
        )}
        {report?.totals_withheld && (
          <Alert severity="warning" sx={{ mb: 2 }}>{report.totals_withheld}</Alert>
        )}

        {totals && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1, mb: 2 }}>
            {[
              ['School-age', totals.school_age],
              ['EI', totals.ei],
              ['Itinerant', totals.itinerant],
              ['Mapped total', totals.all],
              ['Unmapped', totals.unmapped],
            ].map(([label, value]) => (
              <Paper key={label} sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5">{value}</Typography>
              </Paper>
            ))}
          </Box>
        )}

        {totals && fundingMix.length > 0 && (
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align="right">Students</TableCell>
                  <TableCell align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundingMix.map((row) => (
                  <TableRow key={row.code}>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>{GROUP_LABEL[row.group] || row.group}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                    <TableCell align="right">{formatChange(row.change)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {(report?.by_site || []).map((block) => {
          const open = Boolean(openSites[block.site.id]);
          return (
            <Paper key={block.site.id} sx={{ mb: 1.5, p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1">{block.site.name}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    {ROSTER_STATUS_LABEL[block.site.roster_status] || block.site.roster_status}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {siteHeadline(block, report.session?.name)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {block.unmapped != null && (
                    <Button size="small" onClick={() => openDrill(block, 'unmapped', 'Unmapped')}>
                      Unmapped {block.unmapped}
                    </Button>
                  )}
                  {block.totals && (
                    <Button size="small" onClick={() => setOpenSites((prev) => ({ ...prev, [block.site.id]: !open }))}>
                      {open ? 'Hide' : 'Show programs'}
                    </Button>
                  )}
                </Box>
              </Box>
              {open && block.totals && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Program</TableCell>
                      <TableCell>Group</TableCell>
                      <TableCell align="right">Students</TableCell>
                      <TableCell align="right">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {block.by_code.map((row) => (
                      <TableRow key={row.code}>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{GROUP_LABEL[row.group] || row.group}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => openDrill(block, row.code, `${row.code} ${row.label}`)}>
                            {row.count}
                          </Button>
                        </TableCell>
                        <TableCell align="right">{formatChange(row.change)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>Unmapped</TableCell>
                      <TableCell colSpan={2}>Funding code is blank or not in the program list</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openDrill(block, 'unmapped', 'Unmapped')}>
                          {block.unmapped}
                        </Button>
                      </TableCell>
                      <TableCell align="right">{formatChange(block.unmapped_change)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Paper>
          );
        })}
      </Container>

      <Dialog open={Boolean(drill)} onClose={() => setDrill(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {drill?.siteName} — {drill?.label}
          {drill && !drill.loading ? ` (${drill.rows.length})` : ''}
        </DialogTitle>
        <DialogContent>
          {drill?.loading && <CircularProgress size={24} />}
          {drill?.error && <Alert severity="error">{drill.error}</Alert>}
          {drill && !drill.loading && !drill.error && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Last name</TableCell>
                  <TableCell>First name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Service type</TableCell>
                  <TableCell>Funding code</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drill.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>No students.</TableCell>
                  </TableRow>
                )}
                {drill.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.last_name}</TableCell>
                    <TableCell>{row.first_name}</TableCell>
                    <TableCell>{row.class_num}</TableCell>
                    <TableCell>{SERVICE_LABEL[row.service_type] || row.service_type}</TableCell>
                    <TableCell>{row.funding_code || '—'}</TableCell>
                    <TableCell>{row.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EnrollmentDashboard;
